const REPORT_SCROLL_TARGET_PROP = "scrollTargetKey";
const REPORT_DATE_PROP = "dateValue";
const REPORT_TABLE_OF_CONTENTS_TYPE = "ReportTableOfContents";
const REPORT_TABLE_OF_CONTENTS_ELEMENT_ID = "__reportTableOfContents";

type ReportSpecElement = {
  type: string;
  props?: Record<string, unknown>;
  children?: string[];
};

/** The element holding the report's top-level blocks: a lone vertical Page or Stack under the root, else the root. */
export const resolveReportContainerId = (
  rootId: string,
  elements: Record<string, ReportSpecElement>,
): string => {
  const rootChildren = elements[rootId]?.children ?? [];
  const onlyChildId = rootChildren.length === 1 ? rootChildren[0] : undefined;
  const onlyChild = onlyChildId ? elements[onlyChildId] : undefined;
  return onlyChildId &&
    onlyChild?.children &&
    (onlyChild.type === "Page" ||
      (onlyChild.type === "Stack" &&
        onlyChild.props?.direction !== "horizontal"))
    ? onlyChildId
    : rootId;
};

/** A Template Studio banner is an Image flagged `banner: true`. */
export const isReportBannerImage = (element?: ReportSpecElement) =>
  element?.type === "Image" && element.props?.banner === true;

/** Derives non-persisted report navigation and normalizes report chrome order. */
export const prepareReportSpecForRender = <
  T extends {
    root?: string;
    elements?: Record<
      string,
      {
        type: string;
        props?: Record<string, unknown>;
        children?: string[];
      }
    >;
  } | null,
>(
  spec: T,
  reportDateValue?: string,
  /** "auto" derives contents whenever titled sections exist (Herald chat); "authored" only when the spec has one (templates). */
  tableOfContents: "auto" | "authored" = "auto",
): T => {
  if (spec === null) return spec;

  const { elements } = spec;
  if (!elements) return spec;

  let changed = false;
  // Only a table of contents placed in the layout counts; an unplaced leftover element does not.
  const placedElementIds = new Set<string>();
  const placeElement = (elementId: string) => {
    if (placedElementIds.has(elementId)) return;
    placedElementIds.add(elementId);
    for (const childId of elements[elementId]?.children ?? []) {
      placeElement(childId);
    }
  };
  if (spec.root) placeElement(spec.root);
  const authoredTableOfContentsId = [...placedElementIds].find(
    (elementId) => elements[elementId]?.type === REPORT_TABLE_OF_CONTENTS_TYPE,
  );
  const hasTableOfContents = authoredTableOfContentsId !== undefined;
  // ReportHeader already shows the briefing date, so a standalone ReportDate would repeat it.
  const hasReportHeader = Object.values(elements).some(
    (element) => element.type === "ReportHeader",
  );
  const removedElementIds = new Set(
    Object.entries(elements)
      .filter(
        ([, element]) =>
          element.type === REPORT_TABLE_OF_CONTENTS_TYPE ||
          (hasReportHeader && element.type === "ReportDate"),
      )
      .map(([elementId]) => elementId),
  );
  const nextElements = Object.fromEntries(
    Object.entries(elements).flatMap(([elementId, element]) => {
      if (removedElementIds.has(elementId)) {
        changed = true;
        return [];
      }

      const children = element.children?.filter(
        (childId) => !removedElementIds.has(childId),
      );
      if (children && children.length !== element.children?.length) {
        changed = true;
        return [[elementId, { ...element, children }]];
      }

      return [[elementId, element]];
    }),
  ) as typeof elements;

  for (const [elementId, element] of Object.entries(nextElements)) {
    if (element.type === "ReportDate" || element.type === "ReportHeader") {
      const props = element.props ?? {};
      if (props[REPORT_DATE_PROP] !== reportDateValue) {
        changed = true;
        const nextProps = { ...props };
        if (reportDateValue) {
          nextProps[REPORT_DATE_PROP] = reportDateValue;
        } else {
          delete nextProps[REPORT_DATE_PROP];
        }
        nextElements[elementId] = {
          ...element,
          props: nextProps,
        };
      }
    }

    if (element.type !== "ReportSection") continue;

    const props = element.props ?? {};
    if (props[REPORT_SCROLL_TARGET_PROP] === elementId) continue;

    changed = true;
    nextElements[elementId] = {
      ...element,
      props: { ...props, [REPORT_SCROLL_TARGET_PROP]: elementId },
    };
  }

  const rootId = spec.root;
  if (!rootId || !nextElements[rootId]) {
    return changed ? { ...spec, elements: nextElements } : spec;
  }

  const sectionEntries: Array<{ elementId: string; title: string }> = [];
  const visited = new Set<string>();
  const visit = (elementId: string) => {
    if (visited.has(elementId)) return;
    visited.add(elementId);

    const element = nextElements[elementId];
    if (!element) return;
    if (element.type === "ReportSection") {
      const title = element.props?.title;
      if (typeof title === "string" && title.trim().length > 0) {
        sectionEntries.push({ elementId, title: title.trim() });
      }
    }
    for (const childId of element.children ?? []) visit(childId);
  };
  visit(rootId);

  const showTableOfContents =
    tableOfContents === "authored"
      ? hasTableOfContents
      : sectionEntries.length > 0;
  if (!showTableOfContents) {
    return changed ? { ...spec, elements: nextElements } : spec;
  }

  const containerId = resolveReportContainerId(rootId, nextElements);
  const container = nextElements[containerId];
  if (!container) return changed ? { ...spec, elements: nextElements } : spec;

  const children = container.children ?? [];
  // Templates flag their banner, matching the saved order; Herald chat treats leading Images as the banner.
  const isBanner = (childId: string) =>
    tableOfContents === "authored"
      ? isReportBannerImage(nextElements[childId])
      : nextElements[childId]?.type === "Image";
  const firstNonBannerIndex = children.findIndex(
    (childId) => !isBanner(childId),
  );
  const bannerIds =
    firstNonBannerIndex === -1
      ? children
      : children.slice(0, firstNonBannerIndex);
  // Contents follow the header, or the date when there is no header (a date beside a header was dropped above).
  const reportHeaderId =
    children.find(
      (childId) => nextElements[childId]?.type === "ReportHeader",
    ) ??
    children.find((childId) => nextElements[childId]?.type === "ReportDate");
  // Keep the authored style props, such as style and separator; only the entries are derived here.
  nextElements[REPORT_TABLE_OF_CONTENTS_ELEMENT_ID] = {
    type: REPORT_TABLE_OF_CONTENTS_TYPE,
    props: {
      ...(authoredTableOfContentsId
        ? elements[authoredTableOfContentsId]?.props
        : {}),
      sections: sectionEntries,
    },
  };
  nextElements[containerId] = {
    ...container,
    children: [
      ...bannerIds,
      ...(reportHeaderId ? [reportHeaderId] : []),
      REPORT_TABLE_OF_CONTENTS_ELEMENT_ID,
      ...children.filter(
        (childId) => childId !== reportHeaderId && !bannerIds.includes(childId),
      ),
    ],
  };

  return { ...spec, elements: nextElements };
};
